import { create } from 'zustand';

/**
 * The service worker (`public/sw.js`) and the app's install state.
 *
 * The worker is what makes Orbit Sync an installable app (PWA) and what shows
 * notifications on phones: Android Chrome only accepts
 * `registration.showNotification`, and Web Push lands in the worker. It is
 * registered once at startup, on secure origins only - browsers refuse service
 * workers over plain HTTP (localhost excepted).
 */

const SW_URL = '/sw.js';
/** How long to wait for a worker before deciding there is none. */
const READY_TIMEOUT_MS = 4_000;

export function serviceWorkerSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && window.isSecureContext;
}

export function registerServiceWorker(): void {
  if (!serviceWorkerSupported()) {
    return;
  }

  navigator.serviceWorker
    .register(SW_URL, { scope: '/', updateViaCache: 'none' })
    .catch((error: unknown) => console.warn('Service worker registration failed', error));
}

/** The active registration, or `null` when there is none (yet). Never throws. */
export async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!serviceWorkerSupported()) {
    return null;
  }

  const timeout = new Promise<null>((resolve) => {
    window.setTimeout(() => resolve(null), READY_TIMEOUT_MS);
  });

  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } catch {
    return null;
  }
}

/** Running as an installed app (Home Screen / app drawer), not in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone or iPad - iPadOS reports itself as a Mac, so touch tells them apart. */
export function isIos(): boolean {
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );
}

// ---- Messages from the worker -----------------------------------------------

type WorkerMessage =
  | { type: 'orbit-sync:navigate'; url: string }
  | { type: 'orbit-sync:push'; channelId: string | null };

const lastPushAt = new Map<string, number>();

/** When the worker last received a push for `channelId` (0 if never). */
export function lastPushFor(channelId: string): number {
  return lastPushAt.get(channelId) ?? 0;
}

/**
 * Routes notification clicks into the app and records pushes as they arrive.
 * `navigate` is the router's; returns the unsubscribe.
 */
export function listenToServiceWorker(navigate: (url: string) => void): () => void {
  if (!serviceWorkerSupported()) {
    return () => undefined;
  }

  const onMessage = (event: MessageEvent<WorkerMessage>) => {
    const message = event.data;

    if (message.type === 'orbit-sync:navigate' && message.url.startsWith('/')) {
      navigate(message.url);
    } else if (message.type === 'orbit-sync:push' && message.channelId) {
      lastPushAt.set(message.channelId, Date.now());
    }
  };

  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
}

// ---- Install prompt (Chrome, Edge, Samsung Internet) ----------------------------

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  /** The browser's deferred install prompt, when it offered one. */
  prompt: BeforeInstallPromptEvent | null;
  install: () => Promise<void>;
}

export const useInstallStore = create<InstallState>((set, get) => ({
  prompt: null,
  async install(): Promise<void> {
    const prompt = get().prompt;

    if (!prompt) {
      return;
    }

    await prompt.prompt();
    await prompt.userChoice;
    // A prompt can be used once.
    set({ prompt: null });
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep it for the account menu's "Install app" instead of the mini-infobar.
    event.preventDefault();
    useInstallStore.setState({ prompt: event as BeforeInstallPromptEvent });
  });

  window.addEventListener('appinstalled', () => useInstallStore.setState({ prompt: null }));
}
